import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PAY_RHYTHM_LABELS,
  PAY_RHYTHMS,
  PAYROLL_ACCOUNT_EXAMPLES,
  PAYROLL_ACCOUNT_KEYS,
  PAYROLL_ACCOUNT_LABELS,
  TAX_NOTICE,
  type ChartOption,
  type PayrollAccountInfo,
  type PayrollAccountKey,
  type PayrollSetup,
  type PayrollSettings,
  type UpdatePayrollSettingsInput,
} from '@kontor/shared';
import { api, ApiError } from '../../api';
import { InfoHint } from '../../components/InfoHint';
import { bpText, centsText, parseBp, parseCents } from '../../money';
import { useSession } from '../../session';
import './payroll.css';

interface Form {
  defaultRhythm: string;
  payDay: string;
  reminderDays: string;
  healthAdditional: string;
  u1: string;
  u2: string;
  accident: string;
  churchRate: string;
  saxony: boolean;
  wageFloor: string;
  companyName: string;
  companyIban: string;
  companyBic: string;
  wagesAccount: string;
  minijobAccount: string;
  socialAccount: string;
}

const toForm = (s: PayrollSettings): Form => ({
  defaultRhythm: s.defaultRhythm,
  payDay: String(s.payDay),
  reminderDays: String(s.reminderDays),
  healthAdditional: bpText(s.healthAdditionalBp) || '0',
  u1: bpText(s.u1Bp) || '0',
  u2: bpText(s.u2Bp) || '0',
  accident: bpText(s.accidentBp) || '0',
  churchRate: String(s.churchRateBp),
  saxony: s.saxony,
  wageFloor: s.wageFloorCents ? centsText(s.wageFloorCents) : '',
  companyName: s.companyName ?? '',
  companyIban: s.companyIban ?? '',
  companyBic: s.companyBic ?? '',
  wagesAccount: s.wagesAccount ?? '',
  minijobAccount: s.minijobAccount ?? '',
  socialAccount: s.socialAccount ?? '',
});

/** Woher das Konto kommt, das gerade gilt */
function currentText(a: PayrollAccountInfo): string {
  const acc = a.number ? `${a.number}${a.name ? ` ${a.name}` : ''}` : '';
  if (a.source === 'chosen') return a.name ? `Gilt: ${acc}` : `Gilt: ${acc} (steht nicht im Kontenplan)`;
  if (a.source === 'found') return `Ohne Eingabe gilt ${acc}, gefunden über den Namen im Kontenplan.`;
  if (a.source === 'fallback') return `Ohne Eingabe gehen Aushilfslöhne auf das Konto für Löhne und Gehälter (${acc}).`;
  return 'Ohne Eingabe findet Wirkia kein passendes Konto. Der Aufwand stünde im Finanzreport ohne Konto.';
}

/**
 * Einrichtung der Löhne: oben die gesetzlichen Werte, die heute gelten (nur zum Ansehen, sie ändern sich mit dem Gesetz), darunter die Einstellungen des Betriebs. Wirkia prüft die Einstellungen
 * gegen das Gesetz und meldet Widersprüche, auch solche, die erst mit der nächsten Erhöhung des Mindestlohns entstehen.
 */
export function PayrollSetupPage() {
  const { can } = useSession();
  const client = useQueryClient();
  const write = can('payroll.write');
  const setup = useQuery({ queryKey: ['payroll-setup'], queryFn: async () => (await api<{ data: PayrollSetup }>('GET', '/payroll/setup')).data });
  // Konten des Kontenplans als Vorschlag. Ohne das Recht auf den Kontenplan bleibt es bei der Eingabe der Nummer.
  const chart = useQuery({ queryKey: ['chart-options'], queryFn: async () => (await api<{ data: ChartOption[] }>('GET', '/chart/options')).data, enabled: can('chart.read'), retry: false });
  const expenseAccounts = (chart.data ?? []).filter((o) => o.kind === 'expense');
  const [f, setF] = useState<Form | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (setup.data && !f) setF(toForm(setup.data.settings)); }, [setup.data, f]);

  const save = useMutation({
    mutationFn: async () => {
      if (!f) return null;
      const num = (t: string, parse: (v: string) => number | null, field: string) => {
        const v = parse(t);
        if (v === null) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { [field]: 'Bitte eine Zahl angeben.' });
        return v;
      };
      const body: UpdatePayrollSettingsInput = {
        defaultRhythm: f.defaultRhythm as UpdatePayrollSettingsInput['defaultRhythm'],
        payDay: Number(f.payDay),
        reminderDays: Number(f.reminderDays),
        healthAdditionalBp: num(f.healthAdditional, parseBp, 'healthAdditionalBp'),
        u1Bp: num(f.u1, parseBp, 'u1Bp'),
        u2Bp: num(f.u2, parseBp, 'u2Bp'),
        accidentBp: num(f.accident, parseBp, 'accidentBp'),
        churchRateBp: f.churchRate === '800' ? 800 : 900,
        saxony: f.saxony,
        wageFloorCents: f.wageFloor.trim() ? num(f.wageFloor, parseCents, 'wageFloorCents') : null,
        companyName: f.companyName.trim() || null,
        companyIban: f.companyIban.trim() || null,
        companyBic: f.companyBic.trim() || null,
        wagesAccount: f.wagesAccount.trim() || null,
        minijobAccount: f.minijobAccount.trim() || null,
        socialAccount: f.socialAccount.trim() || null,
      };
      return (await api<{ data: PayrollSetup }>('PUT', '/payroll/setup', body)).data;
    },
    onSuccess: (data) => {
      if (!data) return;
      client.setQueryData(['payroll-setup'], data);
      void client.invalidateQueries({ queryKey: ['payroll-overview'] });
      setF(toForm(data.settings));
      setSaved(true);
    },
    onError: (e) => setErrors(e instanceof ApiError && e.status === 422 ? e.fields : { _: e instanceof Error ? e.message : 'Die Einstellungen konnten nicht gespeichert werden.' }),
  });

  const set = (key: keyof Form, value: string | boolean) => { setF((x) => (x ? { ...x, [key]: value } : x)); setSaved(false); };
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);
  const hint = (label: string, text: string) => <InfoHint label={label}>{text}</InfoHint>;
  const s = setup.data;

  return (
    <>
      <h2>Löhne</h2>
      {setup.isLoading && <p className="muted">Wird geladen …</p>}
      {setup.isError && <div className="alert alert--error" role="alert">Die Einrichtung konnte nicht geladen werden.</div>}
      {s && f && (
        <>
          {s.conflicts.length > 0 && (
            <div className="alert alert--warn" role="alert">
              <strong>Widersprüche, die Sie prüfen sollten</strong>
              <ul className="payroll-warnings">{s.conflicts.map((c) => <li key={c}>{c}</li>)}</ul>
            </div>
          )}

          <div className="card">
            <h2>Gesetzliche Werte {s.statutoryYear}</h2>
            <p className="muted small">Diese Werte legt der Gesetzgeber fest, Wirkia pflegt sie. Quelle und Stand: {s.statutorySource}. {TAX_NOTICE}</p>
            <table className="payroll-statutory">
              <tbody>
                {s.statutory.map((v) => (
                  <tr key={v.key}>
                    <td>{v.label}</td>
                    <td>{v.value}</td>
                    <td className="muted small">{v.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <form className="card" onSubmit={(e: FormEvent) => { e.preventDefault(); setErrors({}); save.mutate(); }} noValidate>
            <h2>Einstellungen Ihres Betriebs</h2>
            {errors._ && <div className="alert alert--error" role="alert">{errors._}</div>}
            {saved && <div className="alert alert--success" role="status">Gespeichert.</div>}
            <h3 className="payroll-sub">Auszahlung und Erinnerung</h3>
            <div className="form-grid form-grid--aligned">
              <label className="field">
                <span className="invoice-label">Zahlungsrhythmus der Mitarbeiter{hint('Zahlungsrhythmus', 'Gilt für alle Mitarbeiter, bei denen kein eigener Rhythmus eingetragen ist. Täglich oder wöchentlich ist erlaubt. Nach Vereinbarung ist erlaubt, solange der Lohn spätestens am letzten Bankarbeitstag des Folgemonats ausgezahlt wird.')}</span>
                <select className="input" value={f.defaultRhythm} disabled={!write} onChange={(e) => set('defaultRhythm', e.target.value)}>
                  {PAY_RHYTHMS.map((r) => <option key={r} value={r}>{PAY_RHYTHM_LABELS[r]}</option>)}
                </select>
              </label>
              <label className="field">
                <span className="invoice-label">Auszahlungstag im Monat{hint('Auszahlungstag', '0 bedeutet: am Monatsletzten des abgerechneten Monats. Sonst der gewählte Tag des Folgemonats. Fällt der Tag auf ein Wochenende, zahlt Wirkia einen Werktag davor vor.')}</span>
                <input className="input num" inputMode="numeric" value={f.payDay} disabled={!write} onChange={(e) => set('payDay', e.target.value)} aria-invalid={!!errors.payDay} />
                {err('payDay')}
              </label>
              <label className="field">
                <span className="invoice-label">Erinnerung vorab (Tage){hint('Erinnerung', 'So viele Tage vor dem Auszahlungstag erscheint der Hinweis in der Glocke und auf der Übersicht, wenn der Lohnlauf fehlt oder noch nicht ausgezahlt ist.')}</span>
                <input className="input num" inputMode="numeric" value={f.reminderDays} disabled={!write} onChange={(e) => set('reminderDays', e.target.value)} aria-invalid={!!errors.reminderDays} />
                {err('reminderDays')}
              </label>
              <label className="field">
                <span className="invoice-label">Betrieblicher Mindeststundenlohn €{hint('Betrieblicher Mindestlohn', 'Nur ausfüllen, wenn Ihr Betrieb oder ein Tarif mehr zahlen muss als das Gesetz. Er darf nie unter dem gesetzlichen Mindestlohn liegen. Wirkia lehnt niedrigere Stundenlöhne ab, außer bei Auszubildenden, Praktikanten und begründeten Ausnahmen.')}</span>
                <input className="input num" inputMode="decimal" value={f.wageFloor} disabled={!write} onChange={(e) => set('wageFloor', e.target.value)} placeholder="leer: nur gesetzlich" aria-invalid={!!errors.wageFloorCents} />
                {err('wageFloorCents')}
              </label>
            </div>

            <h3 className="payroll-sub">Beiträge und Steuer</h3>
            <div className="form-grid form-grid--aligned">
              <label className="field">
                <span className="invoice-label">Zusatzbeitrag der Krankenkasse %{hint('Zusatzbeitrag', 'Gilt für Mitarbeiter ohne eigene Angabe bei den Lohnstammdaten. Den Satz jeder Kasse veröffentlicht der GKV-Spitzenverband. Der Durchschnitt 2026 liegt bei 2,9 %.')}</span>
                <input className="input num" inputMode="decimal" value={f.healthAdditional} disabled={!write} onChange={(e) => set('healthAdditional', e.target.value)} aria-invalid={!!errors.healthAdditionalBp} />
                {err('healthAdditionalBp')}
              </label>
              <label className="field">
                <span className="invoice-label">Umlage U1 (Krankheit) %{hint('Umlage U1', 'Betriebe bis 30 Beschäftigte zahlen sie an die Krankenkasse und bekommen Entgeltfortzahlung im Krankheitsfall erstattet. Den Satz nennt Ihre Kasse. 0, wenn Sie nicht teilnehmen.')}</span>
                <input className="input num" inputMode="decimal" value={f.u1} disabled={!write} onChange={(e) => set('u1', e.target.value)} aria-invalid={!!errors.u1Bp} />
                {err('u1Bp')}
              </label>
              <label className="field">
                <span className="invoice-label">Umlage U2 (Mutterschaft) %{hint('Umlage U2', 'Alle Betriebe zahlen sie. Den Satz nennt Ihre Kasse.')}</span>
                <input className="input num" inputMode="decimal" value={f.u2} disabled={!write} onChange={(e) => set('u2', e.target.value)} aria-invalid={!!errors.u2Bp} />
                {err('u2Bp')}
              </label>
              <label className="field">
                <span className="invoice-label">Berufsgenossenschaft %{hint('Berufsgenossenschaft', 'Die Unfallversicherung berechnet die BG jährlich aus der Lohnsumme und der Gefahrklasse. Hier tragen Sie einen Richtwert in Prozent der Lohnsumme ein, damit die Lohnkosten vollständig sind. 0, wenn Sie das nicht einrechnen wollen.')}</span>
                <input className="input num" inputMode="decimal" value={f.accident} disabled={!write} onChange={(e) => set('accident', e.target.value)} aria-invalid={!!errors.accidentBp} />
                {err('accidentBp')}
              </label>
              <label className="field">
                <span className="invoice-label">Kirchensteuersatz{hint('Kirchensteuer', 'Der Satz hängt vom Bundesland der Betriebsstätte ab: 8 % in Bayern und Baden-Württemberg, sonst 9 %.')}</span>
                <select className="input" value={f.churchRate} disabled={!write} onChange={(e) => set('churchRate', e.target.value)}>
                  <option value="900">9 %</option>
                  <option value="800">8 % (Bayern, Baden-Württemberg)</option>
                </select>
                {err('churchRateBp')}
              </label>
              <label className="field field--check">
                <input type="checkbox" checked={f.saxony} disabled={!write} onChange={(e) => set('saxony', e.target.checked)} />
                <span>Betriebsstätte in Sachsen (Pflegeversicherung)</span>
              </label>
            </div>

            <h3 className="payroll-sub">Konto für die Lohnüberweisung</h3>
            <p className="muted small">Lassen Sie die Felder leer, nimmt Wirkia Firmenname, IBAN und BIC aus Einrichtung → Firmendaten. Tragen Sie hier ein anderes Konto ein, gilt dieses für die Überweisungsdatei der Löhne.</p>
            <div className="form-grid form-grid--aligned">
              <label className="field"><span>Name auf dem Konto</span><input className="input" maxLength={70} value={f.companyName} disabled={!write} onChange={(e) => set('companyName', e.target.value)} aria-invalid={!!errors.companyName} />{err('companyName')}</label>
              <label className="field"><span>IBAN</span><input className="input" maxLength={40} value={f.companyIban} disabled={!write} onChange={(e) => set('companyIban', e.target.value)} aria-invalid={!!errors.companyIban} />{err('companyIban')}</label>
              <label className="field"><span>BIC (optional)</span><input className="input" maxLength={11} value={f.companyBic} disabled={!write} onChange={(e) => set('companyBic', e.target.value)} aria-invalid={!!errors.companyBic} />{err('companyBic')}</label>
            </div>
            <p className="muted small">Wirkia bucht nichts ab. Aus einem festgeschriebenen Lohnlauf entsteht eine Überweisungsdatei (SEPA-Sammelüberweisung), die Sie im Online-Banking hochladen und dort freigeben.</p>

            <h3 className="payroll-sub">Sachkonten</h3>
            <p className="muted small">
              Auf diese Konten bucht Wirkia den Lohnaufwand jedes festgeschriebenen Lohnlaufs. Danach richten sich Finanzreport, Sachposten und Auswertungen. Die Nummern nennt Ihr Steuerberater,
              sie hängen vom Kontenrahmen ab. Bleibt ein Feld leer, sucht Wirkia das Konto über den Namen im Kontenplan. Eine Änderung gilt auch für Lohnläufe, die schon festgeschrieben sind.
            </p>
            <div className="form-grid form-grid--aligned">
              {PAYROLL_ACCOUNT_KEYS.map((key: PayrollAccountKey) => {
                const typed = f[key].trim();
                const known = typed ? (chart.data ?? []).find((o) => o.number === typed) : undefined;
                const auto = s.accounts[key].source !== 'chosen' && s.accounts[key].number ? s.accounts[key] : null;
                return (
                  <label key={key} className="field">
                    <span className="invoice-label">
                      {PAYROLL_ACCOUNT_LABELS[key]}
                      {hint(PAYROLL_ACCOUNT_LABELS[key], `Üblich sind im SKR03 ${PAYROLL_ACCOUNT_EXAMPLES[key].skr03}, im SKR04 ${PAYROLL_ACCOUNT_EXAMPLES[key].skr04}.${key === 'minijobAccount' ? ' Leer: Aushilfslöhne gehen auf das Konto für Löhne und Gehälter.' : ''}`)}
                    </span>
                    {expenseAccounts.length > 0 ? (
                      // Mit Kontenplan: Auswahl wie beim Sachkonto der Eingangsrechnung, „Automatisch“ lässt das Feld leer
                      <select className="input" value={typed} disabled={!write} onChange={(e) => set(key, e.target.value)} aria-invalid={!!errors[key]}>
                        <option value="">{auto ? `Automatisch: ${auto.number}${auto.name ? ` ${auto.name}` : ''}` : 'Automatisch (kein passendes Konto gefunden)'}</option>
                        {typed && !expenseAccounts.some((o) => o.number === typed) && <option value={typed}>{typed} {known ? known.name : '(nicht im Kontenplan)'}</option>}
                        {expenseAccounts.map((o) => <option key={o.number} value={o.number}>{o.number} {o.name}</option>)}
                      </select>
                    ) : (
                      <input className="input" inputMode="numeric" maxLength={8} value={f[key]} disabled={!write} onChange={(e) => set(key, e.target.value)} placeholder={auto?.number ?? 'Kontonummer'} aria-invalid={!!errors[key]} />
                    )}
                    {err(key) ?? <small className="muted">{typed ? (known ? '' : chart.data ? 'Steht nicht im Kontenplan.' : '') : currentText(s.accounts[key])}</small>}
                  </label>
                );
              })}
            </div>
            <p className="muted small">Arbeitnehmeranteile, Lohnsteuer und Auszahlung sind kein zusätzlicher Aufwand, sie stecken im Bruttolohn. Wirkia bucht deshalb nur Bruttolohn und Arbeitgeberanteile.</p>
            {write && <div className="form-actions"><button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button></div>}
          </form>
        </>
      )}
    </>
  );
}
